import {
  Box,
  VStack,
  Heading,
  Text,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Badge,
  Flex,
  Icon,
  Button,
} from "@chakra-ui/react"
import { FaBook, FaHeart, FaBrain, FaEye } from "react-icons/fa6"
import { useAppSelector } from "../../app/hooks"
import {
  selectGameEducation,
  selectGameMentalHealth,
  selectGamePhysicalHealth,
  selectHistory,
} from "./gameSlice"
import { ResetButton } from "./SlideViewer"
import { useState } from "react"
import type { CardReviewTone, HistoryEntry } from "./CardReviewDialog"
import { CardReviewDialog, findSlideByTitle } from "./CardReviewDialog"

export const GameSummary = () => {
  const education = useAppSelector(selectGameEducation)
  const mentalHealth = useAppSelector(selectGameMentalHealth)
  const physicalHealth = useAppSelector(selectGamePhysicalHealth)
  const history = useAppSelector(selectHistory)

  // The card dialog: one card from the Best/Most Challenging previews, or
  // every card of the game for the review. Component state only, so nothing
  // is saved and Start Over (which clears history) removes it. The content
  // stays set while the dialog closes, so it does not blank as it fades.
  const [dialog, setDialog] = useState<{
    isOpen: boolean
    entries: HistoryEntry[]
    tone: CardReviewTone
  }>({ isOpen: false, entries: [], tone: "review" })

  // Find best and worst choices based on total impact. Only genuine
  // decisions qualify: the slide must have offered more than one choice
  // (filler/celebration cards have a single Continue-style button), and the
  // impact must actually be positive (best) / negative (most challenging) —
  // otherwise a zero-impact filler card wins the slot in a clean run.
  const decisions = history.filter(h => {
    if (h.totalImpact === undefined) return false
    const slide = h.slide ? findSlideByTitle(h.slide) : null
    return (slide?.selections?.length ?? 0) > 1
  })

  const positives = decisions.filter(h => (h.totalImpact ?? 0) > 0)
  const bestChoice =
    positives.length > 0
      ? positives.reduce((prev, current) =>
          (current.totalImpact ?? 0) > (prev.totalImpact ?? 0) ? current : prev,
        )
      : null

  const negatives = decisions.filter(h => (h.totalImpact ?? 0) < 0)
  const worstChoice =
    negatives.length > 0
      ? negatives.reduce((prev, current) =>
          (current.totalImpact ?? 0) < (prev.totalImpact ?? 0) ? current : prev,
        )
      : null

  const openPreview = (
    choice: typeof bestChoice | typeof worstChoice,
    isBest: boolean,
  ) => {
    if (!choice?.slide || !choice?.action) return
    setDialog({
      isOpen: true,
      entries: [choice],
      tone: isBest ? "best" : "worst",
    })
  }

  // The one way into the card-by-card review. Zach confirmed on 7 October
  // 2026 that it needs no mentor code for now; a code check, if one is
  // wanted later, goes here before the dialog opens.
  const openReview = () => {
    setDialog({ isOpen: true, entries: history, tone: "review" })
  }

  const closeDialog = () => setDialog(d => ({ ...d, isOpen: false }))

  const totalScore = education + mentalHealth + physicalHealth

  return (
    <Box
      minH="100%"
      bgGradient="linear(to-br, #f0f4f8, #e2e8f0, #cbd5e0)"
      p={{ base: 4, md: 6, lg: 8 }}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box maxW="1200px" w="100%">
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Heading size="2xl" mb={2} color="gray.800">
              Game Over
            </Heading>
            <Text fontSize="xl" color="gray.700">
              Your Journey Summary
            </Text>
          </Box>

          {/* Final Stats Card */}
          <Card borderRadius="2xl" boxShadow="2xl" bg="white">
            <CardHeader
              bg="gray.50"
              borderBottom="1px"
              borderColor="gray.200"
              borderTopRadius="2xl"
            >
              <Heading size="md" color="gray.700">
                Final Statistics
              </Heading>
            </CardHeader>
            <CardBody>
              <Grid
                templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                gap={4}
              >
                <GridItem>
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={FaHeart} color="blue.500" />
                    <Text fontWeight="bold">Health</Text>
                  </Flex>
                  <Text fontSize="2xl">{physicalHealth}</Text>
                </GridItem>

                <GridItem>
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={FaBrain} color="blue.500" />
                    <Text fontWeight="bold">Happiness</Text>
                  </Flex>
                  <Text fontSize="2xl">{mentalHealth}</Text>
                </GridItem>

                <GridItem>
                  <Flex align="center" gap={2} mb={2}>
                    <Icon as={FaBook} color="blue.500" />
                    <Text fontWeight="bold">Education</Text>
                  </Flex>
                  <Text fontSize="2xl">{education}</Text>
                </GridItem>
              </Grid>

              <Divider my={4} />

              <Grid
                templateColumns="repeat(auto-fit, minmax(150px, 1fr))"
                gap={4}
              >
                <GridItem>
                  <Text color="gray.600" fontSize="sm">
                    Total Score
                  </Text>
                  <Text fontSize="xl" fontWeight="bold">
                    {totalScore}
                  </Text>
                </GridItem>
              </Grid>
            </CardBody>
          </Card>

          {/* Best and Worst Choices */}
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
            {/* Best Choice */}
            {bestChoice && (
              <Card
                borderTop="4px"
                borderColor="green.500"
                borderRadius="2xl"
                boxShadow="xl"
                bg="white"
              >
                <CardHeader>
                  <Flex align="center" gap={2}>
                    <Badge colorScheme="green" fontSize="md" px={2} py={1}>
                      Best Choice
                    </Badge>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Scenario
                      </Text>
                      <Text fontWeight="bold">{bestChoice.slide}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Your Choice
                      </Text>
                      <Text fontWeight="bold" color="green.600">
                        {bestChoice.action}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Impact
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="green.600">
                        +{bestChoice.totalImpact}
                      </Text>
                    </Box>
                    {bestChoice.effects && (
                      <Box>
                        <Text fontSize="sm" color="gray.600" mb={1}>
                          Effects
                        </Text>
                        <Flex wrap="wrap" gap={2}>
                          {bestChoice.effects.agency && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.agency > 0 ? "green" : "red"
                              }
                            >
                              Agency {bestChoice.effects.agency > 0 ? "+" : ""}
                              {bestChoice.effects.agency}
                            </Badge>
                          )}
                          {bestChoice.effects.education && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.education > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Education{" "}
                              {bestChoice.effects.education > 0 ? "+" : ""}
                              {bestChoice.effects.education}
                            </Badge>
                          )}
                          {bestChoice.effects.health && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.health > 0 ? "green" : "red"
                              }
                            >
                              Health {bestChoice.effects.health > 0 ? "+" : ""}
                              {bestChoice.effects.health}
                            </Badge>
                          )}
                          {bestChoice.effects.relationship && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.relationship > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Relationship{" "}
                              {bestChoice.effects.relationship > 0 ? "+" : ""}
                              {bestChoice.effects.relationship}
                            </Badge>
                          )}
                          {bestChoice.effects.mentalHealth && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.mentalHealth > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Happiness{" "}
                              {bestChoice.effects.mentalHealth > 0 ? "+" : ""}
                              {bestChoice.effects.mentalHealth}
                            </Badge>
                          )}
                          {bestChoice.effects.physicalHealth && (
                            <Badge
                              colorScheme={
                                bestChoice.effects.physicalHealth > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Health{" "}
                              {bestChoice.effects.physicalHealth > 0 ? "+" : ""}
                              {bestChoice.effects.physicalHealth}
                            </Badge>
                          )}
                        </Flex>
                      </Box>
                    )}
                    <Button
                      leftIcon={<Icon as={FaEye} />}
                      colorScheme="green"
                      variant="outline"
                      onClick={() => openPreview(bestChoice, true)}
                      mt={2}
                    >
                      View Preview
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            )}

            {/* Worst Choice */}
            {worstChoice && (
              <Card
                borderTop="4px"
                borderColor="red.500"
                borderRadius="2xl"
                boxShadow="xl"
                bg="white"
              >
                <CardHeader>
                  <Flex align="center" gap={2}>
                    <Badge colorScheme="red" fontSize="md" px={2} py={1}>
                      Most Challenging Choice
                    </Badge>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Scenario
                      </Text>
                      <Text fontWeight="bold">{worstChoice.slide}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Your Choice
                      </Text>
                      <Text fontWeight="bold" color="red.600">
                        {worstChoice.action}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Impact
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="red.600">
                        {worstChoice.totalImpact}
                      </Text>
                    </Box>
                    {worstChoice.effects && (
                      <Box>
                        <Text fontSize="sm" color="gray.600" mb={1}>
                          Effects
                        </Text>
                        <Flex wrap="wrap" gap={2}>
                          {worstChoice.effects.agency && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.agency > 0 ? "green" : "red"
                              }
                            >
                              Agency {worstChoice.effects.agency > 0 ? "+" : ""}
                              {worstChoice.effects.agency}
                            </Badge>
                          )}
                          {worstChoice.effects.education && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.education > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Education{" "}
                              {worstChoice.effects.education > 0 ? "+" : ""}
                              {worstChoice.effects.education}
                            </Badge>
                          )}
                          {worstChoice.effects.health && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.health > 0 ? "green" : "red"
                              }
                            >
                              Health {worstChoice.effects.health > 0 ? "+" : ""}
                              {worstChoice.effects.health}
                            </Badge>
                          )}
                          {worstChoice.effects.relationship && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.relationship > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Relationship{" "}
                              {worstChoice.effects.relationship > 0 ? "+" : ""}
                              {worstChoice.effects.relationship}
                            </Badge>
                          )}
                          {worstChoice.effects.mentalHealth && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.mentalHealth > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Happiness{" "}
                              {worstChoice.effects.mentalHealth > 0 ? "+" : ""}
                              {worstChoice.effects.mentalHealth}
                            </Badge>
                          )}
                          {worstChoice.effects.physicalHealth && (
                            <Badge
                              colorScheme={
                                worstChoice.effects.physicalHealth > 0
                                  ? "green"
                                  : "red"
                              }
                            >
                              Health{" "}
                              {worstChoice.effects.physicalHealth > 0
                                ? "+"
                                : ""}
                              {worstChoice.effects.physicalHealth}
                            </Badge>
                          )}
                        </Flex>
                      </Box>
                    )}
                    <Button
                      leftIcon={<Icon as={FaEye} />}
                      colorScheme="red"
                      variant="outline"
                      onClick={() => openPreview(worstChoice, false)}
                      mt={2}
                    >
                      View Preview
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </Grid>

          {/* Every card of the game, in the order played, read-only */}
          {history.length > 0 && (
            <Button
              leftIcon={<Icon as={FaEye} />}
              colorScheme="purple"
              variant="outline"
              bg="white"
              size="lg"
              onClick={openReview}
            >
              Review all choices
            </Button>
          )}

          {/* Reflection Section */}
          <Card bg="gray.50" borderRadius="2xl" boxShadow="xl">
            <CardBody>
              <VStack spacing={3} align="stretch">
                <Heading size="md">Reflection Time</Heading>
                <Text>Take a moment to discuss with your mentor:</Text>
                <Box pl={4}>
                  <Text>• What choices led to your best outcomes?</Text>
                  <Text>• What would you do differently next time?</Text>
                  <Text>
                    • How do these choices relate to real-life decisions?
                  </Text>
                  <Text>
                    • What did you learn about the consequences of your actions?
                  </Text>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Play Again Button */}
          <ResetButton />
        </VStack>
      </Box>

      <CardReviewDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        entries={dialog.entries}
        tone={dialog.tone}
      />
    </Box>
  )
}
